/**
 * HRDashboard — Smart HR Module
 * 5 tabs: Overview | Directory | KPI & Attendance | Leave | AI Assistant
 * Route: /hr/*
 */

import React, { useState, useEffect, useRef, useCallback } from "react";

// ── Types ──────────────────────────────────────────────────────────────────────

interface HREmployee {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: string;
  is_active: boolean;
  is_board_member: boolean;
  worker_type: string;
  base_salary: number | null;
  target_kpi: number | null;
  off_days_per_month: number;
  kpi_mode: string;
  seasonal_start: string | null;
  seasonal_end: string | null;
}

interface KPIEmployee {
  employee_name: string;
  attendance_rate: number;
  target_kpi: number;
  gap: number;
  status: "on_target" | "warning" | "critical";
  on_time_count: number;
  late_count: number;
  actual_hours_present: number;
  base_salary: number | null;
  worker_type: string;
}

interface LeaveBalance {
  employee_id: string;
  employee_name: string;
  annual: { total: number; used: number; remaining: number };
  sick: { total: number; used: number; remaining: number };
  off_days_per_month: number;
  requests: LeaveReq[];
}

interface LeaveReq {
  id: string;
  leave_type: string;
  start_date: string;
  end_date: string;
  days: number;
  reason: string | null;
  status: string;
  reviewed_by: string | null;
}

interface ChatMessage {
  role: "user" | "assistant";
  content: string;
}

// ── Design tokens ──────────────────────────────────────────────────────────────

const C = {
  bg:       "#060e20",
  card:     "rgba(255,255,255,0.02)",
  accent:   "#ffd6a5",
  success:  "#cfeda2",
  danger:   "#ff6e84",
  warn:     "#ffd6a5",
  muted:    "#6d758b",
  text:     "#dde5ff",
  border:   "rgba(189,178,255,0.15)",
  blur:     "backdrop-filter: blur(60px)",
};

const glass: React.CSSProperties = {
  background: C.card,
  backdropFilter: "blur(60px)",
  WebkitBackdropFilter: "blur(60px)",
  border: `0.5px solid ${C.border}`,
  borderRadius: 16,
  padding: "20px 24px",
};

const TABS = ["Overview", "Directory", "KPI & Attendance", "Leave", "AI Assistant"] as const;
type Tab = typeof TABS[number];

// ── Helpers ────────────────────────────────────────────────────────────────────

function apiFetch(path: string, opts?: RequestInit) {
  return fetch(`/api/employee${path}`, { credentials: "include", ...opts }).then(r => {
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    return r.json();
  });
}

function kpiColor(gap: number) {
  if (gap >= 0)  return C.success;
  if (gap >= -10) return C.warn;
  return C.danger;
}

function statusLabel(s: string) {
  if (s === "on_target") return { label: "On Target", color: C.success };
  if (s === "warning")   return { label: "Warning",   color: C.warn };
  return                        { label: "Critical",  color: C.danger };
}

// ── Sub-components ─────────────────────────────────────────────────────────────

const Badge: React.FC<{ color: string; children: React.ReactNode }> = ({ color, children }) => (
  <span style={{
    background: `${color}20`, color, border: `1px solid ${color}40`,
    borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700,
  }}>{children}</span>
);

const KpiBar: React.FC<{ actual: number; target: number }> = ({ actual, target }) => {
  const pct = Math.min(actual, 100);
  const gap = actual - target;
  const color = kpiColor(gap);
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 4 }}>
        <span style={{ color }}>{actual.toFixed(1)}%</span>
        <span style={{ color: C.muted }}>target {target}%</span>
      </div>
      <div style={{ background: "rgba(255,255,255,0.07)", borderRadius: 4, height: 6, position: "relative" }}>
        <div style={{ width: `${pct}%`, height: "100%", background: color, borderRadius: 4 }} />
        {target > 0 && target <= 100 && (
          <div style={{ position: "absolute", top: -3, left: `${target}%`, width: 2, height: 12, background: "rgba(255,255,255,0.4)", borderRadius: 1 }} />
        )}
      </div>
    </div>
  );
};

// ── Tab: Overview ──────────────────────────────────────────────────────────────

const OverviewTab: React.FC = () => {
  const [summary, setSummary] = useState<string>("");
  const [kpiData, setKpiData] = useState<KPIEmployee[]>([]);
  const [loadingSummary, setLoadingSummary] = useState(true);
  const [loadingKpi, setLoadingKpi] = useState(true);

  useEffect(() => {
    apiFetch("/hr/ai-summary")
      .then(d => setSummary(d.summary))
      .catch(() => setSummary("AI summary unavailable."))
      .finally(() => setLoadingSummary(false));
    apiFetch("/hr/kpi-report?days=7")
      .then(d => setKpiData(d.employees || []))
      .catch(() => {})
      .finally(() => setLoadingKpi(false));
  }, []);

  const alerts = kpiData.filter(e => e.status === "critical");

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* AI Summary */}
      <div style={{ ...glass, borderLeft: `3px solid ${C.accent}` }}>
        <div style={{ fontSize: 12, color: C.muted, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.08em" }}>✨ AI Workforce Summary</div>
        {loadingSummary ? (
          <div style={{ color: C.muted }}>Generating summary…</div>
        ) : (
          <p style={{ color: C.text, margin: 0, lineHeight: 1.7 }}>{summary}</p>
        )}
      </div>

      {/* Alert strip */}
      {alerts.length > 0 && (
        <div style={{ ...glass, borderLeft: `3px solid ${C.danger}`, padding: "14px 20px" }}>
          <div style={{ fontSize: 12, color: C.danger, fontWeight: 700, marginBottom: 8 }}>⚠ CRITICAL — Below Target 3+ Days</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {alerts.map(e => (
              <Badge key={e.employee_name} color={C.danger}>{e.employee_name} ({e.attendance_rate.toFixed(1)}%)</Badge>
            ))}
          </div>
        </div>
      )}

      {/* KPI Health Cards */}
      <div style={{ ...glass }}>
        <h3 style={{ margin: "0 0 16px", color: C.text, fontSize: 15 }}>📊 7-Day KPI Health (Non-Board)</h3>
        {loadingKpi ? (
          <div style={{ color: C.muted }}>Loading…</div>
        ) : kpiData.length === 0 ? (
          <div style={{ color: C.muted }}>No data yet.</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 14 }}>
            {kpiData.map(e => {
              const st = statusLabel(e.status);
              return (
                <div key={e.employee_name} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 12, padding: "14px 16px", border: `1px solid ${st.color}30` }}>
                  <div style={{ fontWeight: 700, color: C.text, marginBottom: 8 }}>{e.employee_name}</div>
                  <KpiBar actual={e.attendance_rate} target={e.target_kpi} />
                  <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                    <Badge color={st.color}>{st.label}</Badge>
                    <span style={{ fontSize: 11, color: C.muted }}>gap {e.gap > 0 ? "+" : ""}{e.gap.toFixed(1)}%</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Tab: Directory ─────────────────────────────────────────────────────────────

const DirectoryTab: React.FC = () => {
  const [employees, setEmployees] = useState<HREmployee[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editEmp, setEditEmp] = useState<HREmployee | null>(null);
  const [form, setForm] = useState<any>({
    name: "", email: "", role: "employee", worker_type: "full_time",
    base_salary: "", target_kpi: "", off_days_per_month: 2,
    is_board_member: false, seasonal_start: "", seasonal_end: "",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    apiFetch("/hr/directory")
      .then(d => setEmployees(d.employees || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => {
    setEditEmp(null);
    setForm({ name: "", email: "", role: "employee", worker_type: "full_time", base_salary: "", target_kpi: "", off_days_per_month: 2, is_board_member: false, seasonal_start: "", seasonal_end: "" });
    setErr(""); setShowModal(true);
  };

  const openEdit = (e: HREmployee) => {
    setEditEmp(e);
    setForm({ ...e, base_salary: e.base_salary ?? "", target_kpi: e.target_kpi ?? "", seasonal_start: e.seasonal_start ?? "", seasonal_end: e.seasonal_end ?? "" });
    setErr(""); setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true); setErr("");
    try {
      const body = {
        ...form,
        base_salary: form.base_salary !== "" ? Number(form.base_salary) : null,
        target_kpi:  form.target_kpi  !== "" ? Number(form.target_kpi)  : null,
        seasonal_start: form.seasonal_start || null,
        seasonal_end:   form.seasonal_end   || null,
      };
      if (editEmp) {
        await apiFetch(`/hr/employees/${editEmp.id}`, { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      } else {
        await apiFetch("/hr/employees", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
      }
      setShowModal(false); load();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const inputStyle: React.CSSProperties = { background: "rgba(255,255,255,0.07)", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontFamily: "Space Grotesk, sans-serif", fontSize: 13, padding: "8px 12px", width: "100%", boxSizing: "border-box" };
  const labelStyle: React.CSSProperties = { color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4, display: "block" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <h3 style={{ margin: 0, color: C.text }}>Employee Directory</h3>
        <button onClick={openAdd} style={{ background: C.accent, border: "none", borderRadius: 8, color: "#0b1326", cursor: "pointer", fontFamily: "Space Grotesk, sans-serif", fontWeight: 700, padding: "8px 18px", fontSize: 13 }}>+ Add Employee</button>
      </div>

      <div style={{ ...glass, padding: 0, overflow: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {["Name", "Role", "Type", "Target KPI", "Salary (AED)", "Board", "Status", ""].map(h => (
                <th key={h} style={{ padding: "12px 14px", textAlign: "left", color: C.muted, fontWeight: 600, fontSize: 11, textTransform: "uppercase" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: 20, color: C.muted, textAlign: "center" }}>Loading…</td></tr>
            ) : employees.map(e => (
              <tr key={e.id} style={{ borderBottom: `1px solid ${C.border}` }}>
                <td style={{ padding: "11px 14px", fontWeight: 600, color: C.text }}>{e.name}</td>
                <td style={{ padding: "11px 14px" }}><Badge color="#bdb2ff">{e.role}</Badge></td>
                <td style={{ padding: "11px 14px", color: C.muted, fontSize: 12 }}>{e.worker_type}</td>
                <td style={{ padding: "11px 14px", color: e.target_kpi ? C.accent : C.muted }}>{e.target_kpi != null ? `${e.target_kpi}%` : "—"}</td>
                <td style={{ padding: "11px 14px", color: C.text }}>{e.base_salary != null ? e.base_salary.toLocaleString() : "—"}</td>
                <td style={{ padding: "11px 14px" }}>{e.is_board_member ? <Badge color={C.accent}>Board</Badge> : <span style={{ color: C.muted }}>—</span>}</td>
                <td style={{ padding: "11px 14px" }}>{e.is_active ? <Badge color={C.success}>Active</Badge> : <Badge color={C.danger}>Inactive</Badge>}</td>
                <td style={{ padding: "11px 14px" }}>
                  <button onClick={() => openEdit(e)} style={{ background: "rgba(255,255,255,0.07)", border: `1px solid ${C.border}`, borderRadius: 6, color: C.text, cursor: "pointer", fontSize: 12, padding: "4px 10px" }}>Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000 }}>
          <div style={{ ...glass, width: 520, maxWidth: "95vw", maxHeight: "90vh", overflowY: "auto" }}>
            <h3 style={{ margin: "0 0 20px", color: C.text }}>{editEmp ? "Edit Employee" : "Add Employee"}</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {[
                { label: "Full Name *", key: "name", type: "text" },
                { label: "Email", key: "email", type: "email" },
                { label: "Role", key: "role", type: "select", opts: ["employee","manager","owner","accountant","labor"] },
                { label: "Worker Type", key: "worker_type", type: "select", opts: ["full_time","seasonal","outsourced"] },
                { label: "Base Salary (AED)", key: "base_salary", type: "number" },
                { label: "Target KPI %", key: "target_kpi", type: "number" },
                { label: "Off Days/Month", key: "off_days_per_month", type: "number" },
              ].map(({ label, key, type, opts }: any) => (
                <div key={key} style={{ gridColumn: key === "name" ? "1 / -1" : undefined }}>
                  <label style={labelStyle}>{label}</label>
                  {type === "select" ? (
                    <select style={inputStyle} value={form[key]} onChange={e => setForm((f: any) => ({ ...f, [key]: e.target.value }))}>
                      {opts.map((o: string) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input style={inputStyle} type={type} value={form[key]} onChange={e => setForm((f: any) => ({ ...f, [key]: e.target.value }))} />
                  )}
                </div>
              ))}
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input type="checkbox" id="board" checked={form.is_board_member} onChange={e => setForm((f: any) => ({ ...f, is_board_member: e.target.checked }))} />
                <label htmlFor="board" style={{ color: C.text, fontSize: 13 }}>Board Member (excluded from KPI)</label>
              </div>
              {form.worker_type === "seasonal" && (
                <>
                  <div>
                    <label style={labelStyle}>Seasonal Start</label>
                    <input style={inputStyle} type="date" value={form.seasonal_start} onChange={e => setForm((f: any) => ({ ...f, seasonal_start: e.target.value }))} />
                  </div>
                  <div>
                    <label style={labelStyle}>Seasonal End</label>
                    <input style={inputStyle} type="date" value={form.seasonal_end} onChange={e => setForm((f: any) => ({ ...f, seasonal_end: e.target.value }))} />
                  </div>
                </>
              )}
            </div>
            {err && <p style={{ color: C.danger, fontSize: 13, marginTop: 12 }}>{err}</p>}
            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button onClick={handleSave} disabled={saving} style={{ background: C.accent, border: "none", borderRadius: 8, color: "#0b1326", cursor: "pointer", fontWeight: 700, padding: "9px 22px", flex: 1 }}>
                {saving ? "Saving…" : (editEmp ? "Save Changes" : "Add Employee")}
              </button>
              <button onClick={() => setShowModal(false)} style={{ background: "rgba(255,255,255,0.07)", border: `1px solid ${C.border}`, borderRadius: 8, color: C.muted, cursor: "pointer", padding: "9px 16px" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Tab: KPI & Attendance ──────────────────────────────────────────────────────

const KpiTab: React.FC = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState<KPIEmployee[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    apiFetch(`/hr/kpi-report?days=${days}`)
      .then(d => setData(d.employees || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const rows = [["Name","Actual %","Target %","Gap","Status","On Time","Late","Hours","Salary"]];
    data.forEach(e => rows.push([e.employee_name, e.attendance_rate.toFixed(1), String(e.target_kpi), e.gap.toFixed(1), e.status, String(e.on_time_count), String(e.late_count), e.actual_hours_present.toFixed(1), String(e.base_salary ?? "")]));
    const csv = rows.map(r => r.join(",")).join("\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    a.download = `kpi_report_${days}d.csv`; a.click();
  };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 10 }}>
        <div style={{ display: "flex", gap: 8 }}>
          {[7, 14, 30, 90].map(d => (
            <button key={d} onClick={() => setDays(d)} style={{ background: days === d ? C.accent : "rgba(255,255,255,0.07)", border: `1px solid ${C.border}`, borderRadius: 7, color: days === d ? "#0b1326" : C.text, cursor: "pointer", fontWeight: 600, padding: "6px 14px", fontSize: 13 }}>{d}d</button>
          ))}
        </div>
        <button onClick={exportCsv} style={{ background: "rgba(255,255,255,0.07)", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, cursor: "pointer", fontSize: 13, padding: "6px 14px" }}>⬇ Export CSV</button>
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {loading ? (
          <div style={{ ...glass, color: C.muted }}>Loading…</div>
        ) : data.length === 0 ? (
          <div style={{ ...glass, color: C.muted }}>No data available.</div>
        ) : data.map(e => {
          const st = statusLabel(e.status);
          return (
            <div key={e.employee_name} style={{ ...glass, padding: "16px 20px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                <div>
                  <span style={{ fontWeight: 700, color: C.text, fontSize: 15 }}>{e.employee_name}</span>
                  <span style={{ marginLeft: 10 }}><Badge color={st.color}>{st.label}</Badge></span>
                </div>
                <div style={{ display: "flex", gap: 16, fontSize: 12, color: C.muted }}>
                  <span>✅ {e.on_time_count} on time</span>
                  <span style={{ color: e.late_count > 0 ? C.danger : C.muted }}>⚠ {e.late_count} late</span>
                  <span>🕐 {e.actual_hours_present.toFixed(1)}h</span>
                  {e.base_salary && <span style={{ color: C.accent }}>💰 {e.base_salary.toLocaleString()} AED</span>}
                </div>
              </div>
              <KpiBar actual={e.attendance_rate} target={e.target_kpi} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Tab: Leave Management ──────────────────────────────────────────────────────

const LeaveTab: React.FC<{ employees: HREmployee[] }> = ({ employees }) => {
  const nonBoard = employees.filter(e => !e.is_board_member && e.is_active);
  const [selectedId, setSelectedId] = useState<string>(nonBoard[0]?.id || "");
  const [balance, setBalance] = useState<LeaveBalance | null>(null);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ leave_type: "annual", start_date: "", end_date: "", reason: "" });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    if (!selectedId) return;
    setLoading(true);
    apiFetch(`/hr/leave/${selectedId}`)
      .then(d => setBalance(d))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [selectedId]);

  const handleRequest = async () => {
    if (!form.start_date || !form.end_date) { setMsg("Please fill start and end dates."); return; }
    setSubmitting(true); setMsg("");
    try {
      await apiFetch(`/hr/leave/${selectedId}/request`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(form) });
      setMsg("Leave request submitted successfully.");
      const d = await apiFetch(`/hr/leave/${selectedId}`);
      setBalance(d);
    } catch (e: any) {
      setMsg(`Error: ${e.message}`);
    } finally { setSubmitting(false); }
  };

  const handleReview = async (empId: string, approved: boolean) => {
    await apiFetch(`/hr/leave/${empId}/approve`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ approved, reviewed_by: "Manager" }) });
    const d = await apiFetch(`/hr/leave/${selectedId}`);
    setBalance(d);
  };

  const inputStyle: React.CSSProperties = { background: "rgba(255,255,255,0.07)", border: `1px solid ${C.border}`, borderRadius: 8, color: C.text, fontFamily: "Space Grotesk, sans-serif", fontSize: 13, padding: "8px 12px", width: "100%", boxSizing: "border-box" };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Employee selector */}
      <div style={{ ...glass }}>
        <label style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", display: "block", marginBottom: 8 }}>Select Employee</label>
        <select style={{ ...inputStyle, maxWidth: 280 }} value={selectedId} onChange={e => setSelectedId(e.target.value)}>
          {nonBoard.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      </div>

      {loading && <div style={{ ...glass, color: C.muted }}>Loading…</div>}
      {balance && (
        <>
          {/* Leave balance */}
          <div style={{ ...glass }}>
            <h3 style={{ margin: "0 0 14px", color: C.text, fontSize: 15 }}>Leave Balance — {balance.employee_name}</h3>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: 12 }}>
              {[
                { label: "Annual Leave", data: balance.annual },
                { label: "Sick Leave",   data: balance.sick },
              ].map(({ label, data }) => (
                <div key={label} style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: "14px 16px" }}>
                  <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", marginBottom: 6 }}>{label}</div>
                  <div style={{ fontSize: 22, fontWeight: 800, color: data.remaining > 5 ? C.success : C.danger }}>{data.remaining}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>days remaining of {data.total}</div>
                </div>
              ))}
              <div style={{ background: "rgba(255,255,255,0.04)", borderRadius: 10, padding: "14px 16px" }}>
                <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", marginBottom: 6 }}>Off Days/Month</div>
                <div style={{ fontSize: 22, fontWeight: 800, color: C.accent }}>{balance.off_days_per_month}</div>
              </div>
            </div>
          </div>

          {/* Request form */}
          <div style={{ ...glass }}>
            <h3 style={{ margin: "0 0 14px", color: C.text, fontSize: 15 }}>Request Leave</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
              <div>
                <label style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", display: "block", marginBottom: 4 }}>Type</label>
                <select style={inputStyle} value={form.leave_type} onChange={e => setForm(f => ({ ...f, leave_type: e.target.value }))}>
                  <option value="annual">Annual</option>
                  <option value="sick">Sick</option>
                  <option value="unpaid">Unpaid</option>
                </select>
              </div>
              <div>
                <label style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", display: "block", marginBottom: 4 }}>Start Date</label>
                <input style={inputStyle} type="date" value={form.start_date} onChange={e => setForm(f => ({ ...f, start_date: e.target.value }))} />
              </div>
              <div>
                <label style={{ color: C.muted, fontSize: 11, textTransform: "uppercase", display: "block", marginBottom: 4 }}>End Date</label>
                <input style={inputStyle} type="date" value={form.end_date} onChange={e => setForm(f => ({ ...f, end_date: e.target.value }))} />
              </div>
            </div>
            <input style={{ ...inputStyle, marginBottom: 12 }} placeholder="Reason (optional)" value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} />
            <button onClick={handleRequest} disabled={submitting} style={{ background: C.accent, border: "none", borderRadius: 8, color: "#0b1326", cursor: "pointer", fontWeight: 700, padding: "9px 22px" }}>
              {submitting ? "Submitting…" : "Submit Request"}
            </button>
            {msg && <p style={{ color: msg.startsWith("Error") ? C.danger : C.success, fontSize: 13, marginTop: 8 }}>{msg}</p>}
          </div>

          {/* Pending approvals */}
          {balance.requests.filter(r => r.status === "pending").length > 0 && (
            <div style={{ ...glass }}>
              <h3 style={{ margin: "0 0 14px", color: C.text, fontSize: 15 }}>Pending Approvals</h3>
              {balance.requests.filter(r => r.status === "pending").map(r => (
                <div key={r.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${C.border}`, flexWrap: "wrap", gap: 10 }}>
                  <div>
                    <Badge color={C.warn}>{r.leave_type}</Badge>
                    <span style={{ color: C.text, marginLeft: 10, fontSize: 13 }}>{r.start_date} → {r.end_date} ({r.days}d)</span>
                    {r.reason && <span style={{ color: C.muted, fontSize: 12, marginLeft: 8 }}>— {r.reason}</span>}
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => handleReview(selectedId, true)} style={{ background: `${C.success}20`, border: `1px solid ${C.success}40`, borderRadius: 6, color: C.success, cursor: "pointer", fontSize: 12, padding: "4px 12px" }}>Approve</button>
                    <button onClick={() => handleReview(selectedId, false)} style={{ background: `${C.danger}20`, border: `1px solid ${C.danger}40`, borderRadius: 6, color: C.danger, cursor: "pointer", fontSize: 12, padding: "4px 12px" }}>Reject</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── Tab: AI HR Assistant ───────────────────────────────────────────────────────

const AITab: React.FC = () => {
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [history]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const msg = input.trim();
    setInput("");
    const newHistory: ChatMessage[] = [...history, { role: "user", content: msg }];
    setHistory(newHistory);
    setLoading(true);
    try {
      const res = await apiFetch("/hr/ai-chat", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: msg, history: newHistory.slice(-6) }),
      });
      setHistory(h => [...h, { role: "assistant", content: res.response }]);
    } catch {
      setHistory(h => [...h, { role: "assistant", content: "Sorry, AI is unavailable right now." }]);
    } finally { setLoading(false); }
  };

  const SUGGESTIONS = [
    "Calculate Hafiz's salary for this month",
    "Who has been late this week?",
    "Generate a warning message for Nadir",
    "Give me a workforce overview",
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "60vh", minHeight: 400 }}>
      {/* Chat history */}
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 12, paddingBottom: 12 }}>
        {history.length === 0 && (
          <div style={{ ...glass, textAlign: "center", padding: "32px 24px" }}>
            <div style={{ fontSize: 28, marginBottom: 12 }}>✨</div>
            <div style={{ color: C.text, fontWeight: 700, marginBottom: 8 }}>HR AI Assistant</div>
            <div style={{ color: C.muted, fontSize: 13, marginBottom: 20 }}>Ask anything about employees, KPI, payroll, or generate warnings.</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, justifyContent: "center" }}>
              {SUGGESTIONS.map(s => (
                <button key={s} onClick={() => setInput(s)} style={{ background: "rgba(255,214,165,0.08)", border: `1px solid rgba(255,214,165,0.2)`, borderRadius: 8, color: C.accent, cursor: "pointer", fontSize: 12, padding: "6px 12px" }}>{s}</button>
              ))}
            </div>
          </div>
        )}
        {history.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start" }}>
            <div style={{
              maxWidth: "75%",
              background: m.role === "user" ? `rgba(255,214,165,0.12)` : "rgba(255,255,255,0.05)",
              border: `1px solid ${m.role === "user" ? "rgba(255,214,165,0.2)" : C.border}`,
              borderRadius: m.role === "user" ? "16px 16px 4px 16px" : "16px 16px 16px 4px",
              padding: "12px 16px",
              color: C.text,
              fontSize: 14,
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
            }}>
              {m.role === "assistant" && <span style={{ fontSize: 11, color: C.muted, display: "block", marginBottom: 4 }}>✨ HR Assistant</span>}
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex" }}>
            <div style={{ background: "rgba(255,255,255,0.05)", border: `1px solid ${C.border}`, borderRadius: "16px 16px 16px 4px", padding: "12px 16px", color: C.muted, fontSize: 13 }}>Thinking…</div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div style={{ display: "flex", gap: 10, paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
        <input
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
          placeholder="Ask about employees, KPI, payroll, warnings…"
          style={{ flex: 1, background: "rgba(255,255,255,0.07)", border: `1px solid ${C.border}`, borderRadius: 10, color: C.text, fontFamily: "Space Grotesk, sans-serif", fontSize: 13, outline: "none", padding: "11px 14px" }}
        />
        <button onClick={send} disabled={loading || !input.trim()} style={{ background: C.accent, border: "none", borderRadius: 10, color: "#0b1326", cursor: "pointer", fontWeight: 700, padding: "0 20px", fontSize: 16 }}>→</button>
      </div>
    </div>
  );
};

// ── Main HRDashboard ───────────────────────────────────────────────────────────

const HRDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState<Tab>("Overview");
  const [employees, setEmployees] = useState<HREmployee[]>([]);

  useEffect(() => {
    apiFetch("/hr/directory").then(d => setEmployees(d.employees || [])).catch(() => {});
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: C.bg, padding: "24px 20px 60px", fontFamily: "Space Grotesk, Manrope, sans-serif", color: C.text, boxSizing: "border-box" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 28 }}>
        <div style={{ fontSize: 28 }}>👥</div>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.text }}>Smart HR</h1>
          <div style={{ fontSize: 12, color: C.muted }}>Directory · KPI · Leave · Payroll · AI</div>
        </div>
      </div>

      {/* Tab bar */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, borderBottom: `1px solid ${C.border}`, paddingBottom: 0, overflowX: "auto" }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setActiveTab(t)} style={{
            background: activeTab === t ? `rgba(255,214,165,0.12)` : "transparent",
            border: "none",
            borderBottom: activeTab === t ? `2px solid ${C.accent}` : "2px solid transparent",
            color: activeTab === t ? C.accent : C.muted,
            cursor: "pointer",
            fontFamily: "Space Grotesk, sans-serif",
            fontWeight: 600,
            fontSize: 13,
            padding: "10px 18px",
            whiteSpace: "nowrap",
            transition: "all 0.15s",
          }}>{t}</button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "Overview"          && <OverviewTab />}
      {activeTab === "Directory"         && <DirectoryTab />}
      {activeTab === "KPI & Attendance"  && <KpiTab />}
      {activeTab === "Leave"             && <LeaveTab employees={employees} />}
      {activeTab === "AI Assistant"      && <AITab />}
    </div>
  );
};

export default HRDashboard;
